<script setup lang="ts">
import {cx} from 'class-variance-authority'
import {motion, type Transition, type TargetAndTransition} from 'motion-v'
import type {Home} from '~/types/legacy'
import type {Populated} from '~/types/populated'

const props = defineProps<{
  data: Populated<Home>['hero']
}>()

const lp = useLocalePath()
const pixelGridLg = [
  '                              ',
  '111111111111111111111111111111',
  '1111111     111111211111111111',
  '111112211111111122121111111111',
  '111111111111111111111111111   ',
  '11111111112      111111122111 ',
  '1111111            1111111111 ',
  '1111111           111111111111',
  '111111                11111111',
  '111111                 1111111',
  '1111111111           111111111',
  '11111111          1111   1111 ',
  '11111                   11111 ',
  '1111111111                1112',
  '111111111          11111111111',
].join('\n')

const gradient = 'text-transparent bg-clip-text bg-gradient-to-b'

const initial: TargetAndTransition = {y: -16, opacity: 0, rotateX: '-15deg'}
const animate: TargetAndTransition = {y: 0, opacity: 1, rotateX: '0deg'}
const transition = (delay = 0): Transition => ({ease: [0.16, 1, 0.3, 1], duration: 0.5, delay})

const lines = computed(() => props.data.headline.split('\n'))
</script>

<template>
  <Container
    class="screen-content pointer-events-none relative inset-0 z-10 flex flex-col items-center gap-y-6 px-6 pb-8 pt-32 md:pb-32 lg:pb-8 lg:pt-48 xl:pb-32"
  >
    <a v-if="data.eyebrow?.show" :href="data.eyebrow.href || '/'" class="pointer-events-auto">
      <Eyebrow :pill="$t('home.hero.eyebrow.pill')" :label="data.eyebrow.label ?? ''" />
    </a>

    <div>
      <Heading size="hero" as="h1" class="text-center">
        <span :class="cx(gradient, 'from-gray-950 to-gray-600 px-1')">{{ lines[0] }}</span>
        <br />
        <span :class="cx(gradient, 'from-main-700 to-main-500 px-1')">{{ lines[1] }}</span>
      </Heading>
    </div>

    <div v-if="data.body" class="mb-12 max-w-2xl">
      <Paragraph size="large" align="center">
        {{ data.body }}
      </Paragraph>
    </div>

    <div class="pointer-events-auto">
      <Action intent="main" size="lg" as="link" :to="lp('/contact')">
        {{ $t('home.hero.cta', 'Get in touch') }}
      </Action>
    </div>

    <div class="pointer-events-auto absolute inset-0 -z-10 h-full w-full px-4 md:px-8">
      <PixelGrid class="h-full" :pattern="pixelGridLg" />
    </div>
  </Container>
</template>
